import { message, Tooltip } from 'antd';
import { useCallback, useRef, useState } from 'react';

import type {
  GroupedImageUploadField,
  ImageUploadField,
} from '@/3.0/ai-viral-template/_components/template-ui/types';
import {
  isSupportedViralTemplateImageType,
  VIRAL_TEMPLATE_IMAGE_UPLOAD_ACCEPT,
  VIRAL_TEMPLATE_IMAGE_UPLOAD_RULES,
} from '@/3.0/ai-viral-template/constants/imageUploadConstraints';
import Loading from '@/components/common/Loading';
import { uploadFile } from '@/components/VideoClipper/utils/ffmpeg/utils';
import type { UseTranslateType } from '@/hooks/useTranslate';
import { AIFunction } from '@/utils/const';
import { EventNewID } from '@/utils/eventlog/eventNewConfig';
import { trackEvent } from '@/utils/eventlog/logAdapter';
import { getImageInfo } from '@/utils/fileUtil';

import { RequiredMark } from './TemplateFormFields';

const CELL_PX = 88;

const BRAND_BORDER = '#4931FA';

export type ImageSlotConstraints = Pick<
  typeof VIRAL_TEMPLATE_IMAGE_UPLOAD_RULES,
  | 'minWidth'
  | 'maxWidth'
  | 'minHeight'
  | 'maxHeight'
  | 'maxSizeBytes'
  | 'minAspectRatio'
  | 'maxAspectRatio'
  | 'minAspectRatioText'
  | 'maxAspectRatioText'
>;

function bytesToMb(size: number): number {
  return Math.round((size / (1024 * 1024)) * 100) / 100;
}

function UploadRequirementTooltip({
  constraints,
  t,
  children,
}: {
  constraints: ImageSlotConstraints;
  t: UseTranslateType;
  children: React.ReactNode;
}) {
  const tooltip = (
    <div className="p-[12px] text-[#111]">
      <div className="mb-[6px] text-[12px] font-semibold leading-[20px]">
        {t('viralTemplate.form.uploadRestrictions')}
      </div>
      <ul className="m-0 list-disc pl-[18px]">
        <li>
          {t('viralTemplate.form.imageFileSizeRangeInvalid', {
            min: 0,
            max: bytesToMb(constraints.maxSizeBytes),
          })}
        </li>
        <li>
          {t('viralTemplate.form.imageWidthRangeInvalid', {
            min: constraints.minWidth,
            max: constraints.maxWidth,
          })}
        </li>
        <li>
          {t('viralTemplate.form.imageHeightRangeInvalid', {
            min: constraints.minHeight,
            max: constraints.maxHeight,
          })}
        </li>
        <li>
          {t('viralTemplate.form.imageRatioInvalid', {
            min: constraints.minAspectRatioText,
            max: constraints.maxAspectRatioText,
          })}
        </li>
      </ul>
    </div>
  );

  return (
    <Tooltip
      title={tooltip}
      placement="top"
      mouseEnterDelay={1}
      align={{
        offset: [15, -10],
      }}
      color="#D6DFFD"
      styles={{
        body: {
          fontSize: '12px',
          lineHeight: '20px',
          padding: 0,
          width: 'fit-content',
        },
      }}
    >
      {children}
    </Tooltip>
  );
}

function loadImageDims(src: string): Promise<{ w: number; h: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = src;
  });
}

function useValidateAndUploadImage(
  constraints: ImageSlotConstraints,
  t: UseTranslateType,
) {
  return useCallback(
    async (file: File, onOk: (assetUrl: string) => void) => {
      if (!isSupportedViralTemplateImageType(file)) {
        void message.error(
          t('rejectedNames-accept', {
            rejectedNames: file.name,
            accept: VIRAL_TEMPLATE_IMAGE_UPLOAD_ACCEPT,
          }),
        );
        return;
      }

      const c = constraints;
      if (file.size > c.maxSizeBytes) {
        void message.error(
          t('viralTemplate.form.imageFileSizeRangeInvalid', {
            min: 0,
            max: 10,
          }),
        );
        return;
      }

      const objUrl = URL.createObjectURL(file);
      try {
        const { w, h } = await loadImageDims(objUrl);
        if (w < c.minWidth || w > c.maxWidth) {
          void message.error(
            t('viralTemplate.form.imageWidthRangeInvalid', {
              min: c.minWidth,
              max: c.maxWidth,
            }),
          );
          return;
        }
        if (h < c.minHeight || h > c.maxHeight) {
          void message.error(
            t('viralTemplate.form.imageHeightRangeInvalid', {
              min: c.minHeight,
              max: c.maxHeight,
            }),
          );
          return;
        }
        const ratio = w / h;
        if (ratio < c.minAspectRatio || ratio > c.maxAspectRatio) {
          void message.error(
            t('viralTemplate.form.imageRatioInvalid', {
              min: c.minAspectRatioText,
              max: c.maxAspectRatioText,
            }),
          );
          return;
        }
      } catch {
        void message.error(t('viralTemplate.form.imageLoadFailed'));
        return;
      } finally {
        URL.revokeObjectURL(objUrl);
      }

      try {
        const url = await uploadFile(file);
        if (!url?.trim()) {
          void message.error(t('merror.tip.uploadFailed'));
          return;
        }
        onOk(url.trim());
      } catch {
        void message.error(t('merror.tip.uploadFailed'));
      }
    },
    [constraints, t],
  );
}

const ReloadIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M10 16H5V21M14 8H19V3M4.58301 9.0034C5.14369 7.61566 6.08244 6.41304 7.29255 5.53223C8.50266 4.65141 9.93686 4.12752 11.4298 4.02051C12.9227 3.9135 14.4147 4.2274 15.7381 4.92661C17.0615 5.62582 18.1612 6.68254 18.9141 7.97612M19.4176 14.9971C18.8569 16.3848 17.9181 17.5874 16.708 18.4682C15.4979 19.3491 14.0652 19.8723 12.5723 19.9793C11.0794 20.0863 9.58606 19.7725 8.2627 19.0732C6.93933 18.374 5.83882 17.3175 5.08594 16.0239"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
    <path
      d="M9.33305 6.66667V11.3333M6.66638 6.66667V11.3333M3.99972 4V11.8667C3.99972 12.6134 3.99972 12.9865 4.14504 13.2717C4.27287 13.5226 4.4767 13.727 4.72758 13.8548C5.01252 14 5.38571 14 6.13099 14H9.86844C10.6137 14 10.9864 14 11.2713 13.8548C11.5222 13.727 11.7267 13.5226 11.8545 13.2717C11.9997 12.9868 11.9997 12.614 11.9997 11.8687V4M3.99972 4H5.33305M3.99972 4H2.66638M5.33305 4H10.6664M5.33305 4C5.33305 3.37874 5.33305 3.06827 5.43454 2.82324C5.56987 2.49654 5.82926 2.23682 6.15597 2.10149C6.40099 2 6.71179 2 7.33305 2H8.66638C9.28764 2 9.59827 2 9.84329 2.10149C10.17 2.23682 10.4295 2.49654 10.5648 2.82324C10.6663 3.06827 10.6664 3.37875 10.6664 4M10.6664 4H11.9997M11.9997 4H13.333"
      stroke="white"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function FilledSlot({
  url,
  slotIndex,
  accept,
  t,
  constraints,
  pickFileForSlot,
  onClear,
  isUploading,
}: {
  url: string;
  slotIndex: number;
  accept: string;
  t: UseTranslateType;
  constraints: ImageSlotConstraints;
  pickFileForSlot: (index: number, file: File) => void;
  onClear: () => void;
  isUploading: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  const onReplaceClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    inputRef.current?.click();
  };

  const onDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClear();
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (f) pickFileForSlot(slotIndex, f);
  };

  return (
    <UploadRequirementTooltip constraints={constraints} t={t}>
      <div
        className="group relative shrink-0 overflow-hidden rounded-[6px] border-[1.5px] border-solid bg-[#FAFCFE]"
        style={{ width: CELL_PX, height: CELL_PX, borderColor: BRAND_BORDER }}
      >
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept={accept}
          onChange={onChange}
        />
        <img src={url} alt="" className="absolute inset-0 size-full object-contain" />
        {isUploading ? (
          <div
            className="absolute inset-0 z-40 flex items-center justify-center rounded-[6px] bg-black/45"
            aria-busy
            aria-label={t('merror.language.uploading')}
          >
            <Loading size={16} />
          </div>
        ) : null}
        <div
          className="pointer-events-none absolute inset-0 rounded-[6px] bg-black/0 transition-colors duration-150 group-hover:bg-black/40"
          aria-hidden
        />
        <button
          type="button"
          className="absolute left-1/2 top-1/2 z-20 flex size-[30px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-[4px] bg-[rgba(255,255,255,0.34)] opacity-0 backdrop-blur-[2px] transition-opacity duration-150 group-hover:pointer-events-auto group-hover:opacity-100"
          onClick={onReplaceClick}
        >
          <ReloadIcon />
        </button>
        <button
          type="button"
          className="absolute right-[4px] top-[4px] z-30 flex size-[20px] items-center justify-center rounded-[4px] bg-black/40 opacity-0 transition-opacity duration-150 group-hover:pointer-events-auto group-hover:opacity-100"
          onClick={onDeleteClick}
        >
          <TrashIcon />
        </button>
      </div>
    </UploadRequirementTooltip>
  );
}

function EmptySlot({
  t,
  slotIndex,
  accept,
  constraints,
  pickFileForSlot,
  isUploading,
}: {
  t: UseTranslateType;
  slotIndex: number;
  accept: string;
  constraints: ImageSlotConstraints;
  pickFileForSlot: (index: number, file: File) => void;
  isUploading: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  const onPick = () => {
    inputRef.current?.click();
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (f) pickFileForSlot(slotIndex, f);
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={accept}
        onChange={onChange}
      />
      <UploadRequirementTooltip constraints={constraints} t={t}>
        <button
          type="button"
          onClick={onPick}
          disabled={isUploading}
          className="flex relative shrink-0 flex-col items-center justify-center gap-[2px] rounded-[4px] border border-dashed border-[#DCDCDC] bg-[#F3F3F3] text-[#11111199] transition-colors hover:border-[#4931FA]/50 hover:border-solid hover:text-[#5959e5] disabled:pointer-events-none disabled:opacity-60"
          style={{ width: CELL_PX, height: CELL_PX }}
          aria-busy={isUploading}
          aria-label={isUploading ? t('merror.language.uploading') : undefined}
        >
          {isUploading ? (
            <Loading size={18} />
          ) : (
            <>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                className="shrink-0"
                aria-hidden
              >
                <path
                  d="M15.9295 9.94498H9.94449V15.93C9.94449 16.278 9.52145 16.875 8.99949 16.875C8.47785 16.875 8.05449 16.278 8.05449 15.93V9.94498H2.06951C1.72175 9.94498 1.12451 9.52193 1.12451 8.99998C1.12451 8.47803 1.72175 8.05498 2.06951 8.05498H8.05449V2.07C8.05449 1.72224 8.47785 1.125 8.99949 1.125C9.52145 1.125 9.94449 1.72224 9.94449 2.07V8.05498H15.9295C16.2772 8.05498 16.8745 8.47803 16.8745 8.99998C16.8745 9.52193 16.2772 9.94498 15.9295 9.94498Z"
                  fill="currentColor"
                />
              </svg>
              <span className="text-[14px] font-medium leading-[20px] text-inherit">
                {t('merror.operate.upload')}
              </span>
            </>
          )}
        </button>
      </UploadRequirementTooltip>
    </>
  );
}

function ImageSlotsRow({
  urls,
  setUrls,
  accept,
  t,
  constraints,
}: {
  urls: string[];
  setUrls: React.Dispatch<React.SetStateAction<string[]>>;
  accept: string;
  t: UseTranslateType;
  constraints: ImageSlotConstraints;
}) {
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const validateAndUpload = useValidateAndUploadImage(constraints, t);

  const pickFileForSlot = useCallback(
    async (index: number, file: File) => {
      setUploadingIndex(index);
      try {
        await validateAndUpload(file, (assetUrl) => {
          getImageInfo(assetUrl).then(({ width, height }) => {
            const resolution = `${width}x${height}`;
            trackEvent(EventNewID.ASSET_UPLOAD, {
              resolution,
              source: AIFunction.ViralTemplate,
            });
          });

          setUrls((prev) => {
            const next = [...prev];
            next[index] = assetUrl;
            return next;
          });
        });
      } finally {
        setUploadingIndex(null);
      }
    },
    [validateAndUpload, setUrls],
  );

  const clearSlot = useCallback(
    (index: number) => {
      setUrls((prev) => {
        const next = [...prev];
        next[index] = '';
        return next;
      });
    },
    [setUrls],
  );

  return (
    <div className="flex flex-wrap items-start gap-[16px]">
      {urls.map((url, i) =>
        url ? (
          <FilledSlot
            key={`slot-${i}-${url.slice(0, 32)}`}
            url={url}
            slotIndex={i}
            accept={accept}
            t={t}
            constraints={constraints}
            pickFileForSlot={pickFileForSlot}
            onClear={() => clearSlot(i)}
            isUploading={uploadingIndex === i}
          />
        ) : (
          <EmptySlot
            key={`empty-${i}`}
            slotIndex={i}
            accept={accept}
            t={t}
            constraints={constraints}
            pickFileForSlot={pickFileForSlot}
            isUploading={uploadingIndex === i}
          />
        ),
      )}
    </div>
  );
}

/** 多槽参考图（默认预览 + 上传 / hover 替换与删除） */
export function GroupedImageUploadFieldRow({
  field,
  value,
  onChange,
  t,
}: {
  field: GroupedImageUploadField;
  /** 长度与 `slots` 一致，由父组件统一维护 */
  value: string[];
  onChange: (next: string[]) => void;
  t: UseTranslateType;
}) {
  const urls = field.slots.map((_, i) => String(value[i] ?? '').trim());
  const setUrls = useCallback(
    (next: React.SetStateAction<string[]>) => {
      const resolved = typeof next === 'function' ? next(urls) : next;
      onChange(field.slots.map((_, i) => String(resolved[i] ?? '').trim()));
    },
    [field.slots, onChange, urls],
  );

  const constraints: ImageSlotConstraints = {
    ...VIRAL_TEMPLATE_IMAGE_UPLOAD_RULES,
  };

  return (
    <div className="flex w-full flex-col gap-[8px]">
      <div className="flex h-[20px] items-center">
        <span className="whitespace-nowrap text-[14px] font-medium leading-[20px] text-[#111]">
          {t(field.label)}
          {field.required ? <RequiredMark /> : null}
        </span>
      </div>
      <ImageSlotsRow
        urls={urls}
        setUrls={setUrls}
        accept={VIRAL_TEMPLATE_IMAGE_UPLOAD_ACCEPT}
        constraints={constraints}
        t={t}
      />
    </div>
  );
}

/** 单图：同一套 88×88 交互，仅 1 槽 */
export function SingleImageUploadField({
  field,
  value,
  onChange,
  t,
}: {
  field: ImageUploadField;
  value: string;
  onChange: (next: string) => void;
  t: UseTranslateType;
}) {
  const urls: string[] = [String(value ?? '').trim()];
  const setUrls = useCallback(
    (next: React.SetStateAction<string[]>) => {
      const prev = urls;
      const resolved = typeof next === 'function' ? next(prev) : next;
      onChange(String(resolved[0] ?? '').trim());
    },
    [onChange, urls],
  );

  const constraints: ImageSlotConstraints = {
    ...VIRAL_TEMPLATE_IMAGE_UPLOAD_RULES,
  };

  return (
    <div className="flex w-full flex-col gap-[8px]">
      <div className="flex h-[20px] items-center">
        <span className="whitespace-nowrap text-[14px] font-medium leading-[20px] text-[#111]">
          {t(field.label)}
          {field.required ? <RequiredMark /> : null}
        </span>
      </div>
      <ImageSlotsRow
        urls={urls}
        setUrls={setUrls}
        accept={VIRAL_TEMPLATE_IMAGE_UPLOAD_ACCEPT}
        constraints={constraints}
        t={t}
      />
    </div>
  );
}
